import { BffReport, Project } from "product-test-types";
import type { FieldError } from "react-hook-form";

import { STRAPI_URL } from "../env";

export type ProjectValidationErrors = {
  validationErrors: Record<string, FieldError>;
};

type ApiErrorResponse = {
  message?: string;
  errors?: Record<string, FieldError>;
};

async function buildRequestErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  const responseText = await response.text();

  if (!responseText) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(responseText) as ApiErrorResponse;
    const rootMessage = parsed?.message || fallback;
    const errorEntries = Object.entries(parsed?.errors ?? {});

    if (errorEntries.length === 0) {
      return rootMessage;
    }

    const details = errorEntries
      .slice(0, 5)
      .map(([field, err]) => `${field}: ${err?.message ?? "Invalid value"}`)
      .join(" | ");

    return `${rootMessage} - ${details}`;
  } catch {
    return `${fallback} - ${responseText}`;
  }
}

export async function fetchProject(project: {
  id: string;
}): Promise<BffReport | undefined> {
  const { id } = project;
  const url = `${STRAPI_URL}/project/${id}`;

  try {
    const response = await fetch(url, {
      credentials: "include",
    });

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    const json = (await response.json()) as Project;

    return { project: json };
  } catch {
    return;
  }
}

export async function fetchAllProjects(): Promise<Project[] | undefined> {
  try {
    const response = await fetch(`${STRAPI_URL}/project`, {
      credentials: "include",
    });

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    const json = (await response.json()) as Project[];

    return json;
  } catch {
    return;
  }
}

export async function updateProject(
  id: number | string | undefined,
  project: Project,
  isOnReport: boolean,
  allowIncompleteAssessment?: boolean,
  submit = false
): Promise<Project | ProjectValidationErrors> {
  const requestBody = {
    project,
    isOnReport,
    ...(allowIncompleteAssessment !== undefined
      ? { allowIncompleteAssessment }
      : {}),
    ...(submit ? { submit: true } : {}),
  };

  const response = await fetch(`${STRAPI_URL}/project/${String(id)}`, {
    body: JSON.stringify(requestBody),
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    method: "PUT",
  });

  if (response.status === 400) {
    const body = (await response.json()) as ApiErrorResponse;
    return { validationErrors: body.errors ?? {} };
  }

  if (!response.ok) {
    throw new Error(
      await buildRequestErrorMessage(
        response,
        `Save project failed with status ${response.status}.`
      )
    );
  }

  const json = (await response.json()) as Project;
  return json;
}

export async function createProject(project: Project): Promise<Project> {
  const response = await fetch(`${STRAPI_URL}/project`, {
    body: JSON.stringify(project),
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(
      await buildRequestErrorMessage(
        response,
        `Save project failed with status ${response.status}.`
      )
    );
  }

  const json = (await response.json()) as Project;
  return json;
}

export async function deleteProject(
  id: number | string | undefined
): Promise<void> {
  try {
    const response = await fetch(`${STRAPI_URL}/project/${String(id)}`, {
      credentials: "include",
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`delete project failed with status ${response.status}.`);
    }
  } catch {
    return;
  }
}

export async function deleteAllProjects(): Promise<void> {
  try {
    const response = await fetch(`${STRAPI_URL}/project`, {
      credentials: "include",
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(
        `delete all projects failed with status ${response.status}.`
      );
    }
  } catch {
    return;
  }
}
